import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import { toast } from 'react-toastify';
import { IconX, IconZoomIn, IconZoomOut, IconChevronLeft, IconChevronRight, IconCheck } from '@tabler/icons-react';
import { uploadToCloudinary } from '../../utils/cloudinary';
import './ImageCropModal.css';

const ImageCropModal = ({
  isOpen,
  onClose,
  imageFile,
  onCropComplete,
  // cropShape determines the crop appearance
  cropShape = 'rect', // 'rect' | 'round'
  // when true, this component uploads cropped result to Cloudinary and returns a URL via onCropComplete
  // when false, it returns a Blob via onCroppedBlob and leaves uploading to the caller
  autoUpload = true,
  onCroppedBlob, // optional: invoked with Blob when autoUpload=false
  aspectRatio = 1, // 0 = free-form, 1 = square, 16/9 = widescreen, etc.
  uploadFolder = 'user_photos/avatars',
  maxSize = { width: 512, height: 512 },
  // NEW: Allow hybrid crop behavior
  allowFreeResize = false, // When true, user can adjust aspect ratio
  minZoom = 0.5,
  maxZoom = 3,
  // NEW: Preview mode - just crop, don't upload
  isPreviewMode = false
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [sourceBlob, setSourceBlob] = useState(null);
  const [isImageReady, setIsImageReady] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const cleanupRef = useRef(null);
  const initCounterRef = useRef(0);
  
  // Reset all states when modal closes OR when new imageFile provided
  useEffect(() => {
    console.log('ImageCropModal: Reset trigger - isOpen:', isOpen, 'imageFile:', !!imageFile);
    
    // Always reset states when modal closes
    if (!isOpen) {
      console.log('ImageCropModal: Modal closed, resetting states');
      setCrop({ x: 0, y: 0 });
      setRotation(0);
      setZoom(1);
      setCroppedAreaPixels(null);
      setIsUploading(false);
      setSourceBlob(null);
      setIsImageReady(false);
      setLoadingImage(false);
      setImageUrl(null);
      
      // Cleanup any existing blob URL immediately when closing
      if (cleanupRef.current) {
        const cleanup = cleanupRef.current;
        cleanup();
        cleanupRef.current = null;
      }
      return; // Exit early when modal is closed
    }

    // When modal is open and we have a new imageFile, reset for new image
    if (isOpen && imageFile) {
      initCounterRef.current += 1; // Increment counter for fresh start
      console.log('ImageCropModal: New image provided, resetting states for new image. Counter:', initCounterRef.current);
      setCrop({ x: 0, y: 0 });
      setRotation(0);
      setZoom(1);
      setCroppedAreaPixels(null);
      setIsUploading(false);
      // Don't reset these immediately - let prepare() handle them
      // setSourceBlob(null);
      // setIsImageReady(false);
      // setImageUrl(null);
    }
  }, [isOpen, imageFile]);

  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;
    
    // Skip if modal is not open or no imageFile
    if (!isOpen || !imageFile) {
      console.log('ImageCropModal: useEffect triggered but modal not open or no imageFile');
      return;
    }
    
    // Add small delay to ensure state is settled before processing
    timeoutId = setTimeout(() => {
      if (cancelled) return;
    
    const prepare = async () => {
      const currentCounter = initCounterRef.current;
      console.log('ImageCropModal: Starting prepare', { 
        imageFile: !!imageFile, 
        isOpen, 
        fileName: imageFile?.name,
        fileSize: imageFile?.size,
        counter: currentCounter
      });
      
      if (!imageFile) {
        console.log('ImageCropModal: No imageFile provided');
        setLoadingImage(false);
        return;
      }
      
      // Cleanup previous URL immediately if exists
      if (cleanupRef.current) {
        const oldCleanup = cleanupRef.current;
        if (!cancelled) {
          oldCleanup();
        }
        cleanupRef.current = null;
      }
      
      // Set loading states (don't reset crop/zoom/rotation here - handled by reset useEffect)
      console.log('ImageCropModal: Setting loading states');
      setLoadingImage(true);
      setIsImageReady(false);
      setImageUrl(null);
      setSourceBlob(null);

      try {
        let src = imageFile;
        const type = (imageFile.type || '').toLowerCase();
        
        // Handle HEIC/HEIF conversion first if needed
        if (type === 'image/heic' || type === 'image/heif') {
          try {
            const mod = await import('heic2any');
            const converted = await mod.default({ 
              blob: imageFile, 
              toType: 'image/jpeg', 
              quality: 0.9 
            });
            src = Array.isArray(converted) ? converted[0] : converted;
            console.log('ImageCropModal: HEIC converted successfully');
          } catch (e) {
            console.warn('HEIC conversion failed; using original file', e);
          }
        }
        
        // Check if component was unmounted during conversion
        if (cancelled) return;
        
        // Create blob URL and set states
        const url = URL.createObjectURL(src);
        console.log('ImageCropModal: Created blob URL:', url);
        
        // Set image states directly without nested helper function to avoid closure issues
        setTimeout(() => {
          if (!cancelled && currentCounter === initCounterRef.current) {
            setImageUrl(url);
            setSourceBlob(src);
            setLoadingImage(false);
            
            // Store cleanup function
            cleanupRef.current = () => {
              console.log('ImageCropModal: Cleaning up blob URL:', url);
              URL.revokeObjectURL(url);
            };
            
            // Trigger resize after setting URL to ensure dimensions are calculated
            setTimeout(() => {
              if (!cancelled && currentCounter === initCounterRef.current) {
                console.log('ImageCropModal: Triggering resize for proper dimensions');
                window.dispatchEvent(new Event('resize'));
              }
            }, 100);
          }
        }, 50);
        
      } catch (error) {
        console.error('Error preparing image:', error);
        if (!cancelled && currentCounter === initCounterRef.current) {
          // Fallback to original file
          const fallbackUrl = URL.createObjectURL(imageFile);
          setTimeout(() => {
            if (!cancelled && currentCounter === initCounterRef.current) {
              setImageUrl(fallbackUrl);
              setSourceBlob(imageFile);
              setLoadingImage(false);
              
              // Store cleanup function for fallback
              cleanupRef.current = () => {
                console.log('ImageCropModal: Cleaning up fallback blob URL:', fallbackUrl);
                URL.revokeObjectURL(fallbackUrl);
              };
            }
          }, 50);
        }
      }
    };
    
    prepare();
    }, 10); // Small delay to ensure React state updates are complete
    
    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Don't cleanup here - let the reset useEffect handle it immediately when modal closes
      // This prevents race conditions where cleanup happens while modal is reopening
    };
  }, [imageFile, isOpen]);
  
  // Force resize of cropper when image becomes ready (additional trigger for safety)
  useEffect(() => {
    if (isImageReady && imageUrl) {
      console.log('ImageCropModal: Image ready, final resize trigger');
      // Additional resize trigger when cropper confirms image is loaded
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 150); // Slightly delayed to ensure cropper is fully ready
    }
  }, [isImageReady, imageUrl]);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onCropAreaChange = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const rotateLeft = useCallback(() => {
    setRotation((prev) => prev - 90);
  }, []);

  const rotateRight = useCallback(() => {
    setRotation((prev) => prev + 90);
  }, []);

  const handleCropComplete = useCallback(async () => {
    if (!imageFile && !sourceBlob) return;

    try {
      setIsUploading(true);

      // Validate crop area
      if (!croppedAreaPixels) {
        setIsUploading(false);
        toast.error('Please select a crop area before continuing.');
        return;
      }

      // Use sourceBlob (converted) if available, otherwise fall back to imageFile
      const imageSource = sourceBlob || imageFile;
      if (!imageSource) {
        setIsUploading(false);
        toast.error('No image source available. Please try selecting the image again.');
        return;
      }
      
      const croppedBlob = await getCroppedImage(
        imageSource,
        croppedAreaPixels,
        rotation,
        maxSize
      );

      if (!croppedBlob) {
        setIsUploading(false);
        toast.error('Cropping failed. Please adjust the crop area and try again.');
        return;
      }

      // Handle different modes: preview, auto-upload, or custom upload
      if (isPreviewMode) {
        // Preview mode: return blob without uploading
        onCropComplete && onCropComplete(croppedBlob);
      } else if (autoUpload) {
        // Auto-upload mode: upload to Cloudinary and return URL
        const url = await uploadToCloudinary(croppedBlob, {
          folder: uploadFolder,
          compress: true,
          maxWidth: maxSize.width,
          maxHeight: maxSize.height,
          quality: 0.8
        });
        onCropComplete && onCropComplete(url);
      } else {
        // Custom upload mode: return blob to caller
        onCroppedBlob && onCroppedBlob(croppedBlob);
      }

      onClose();
    } catch (error) {
      console.error('Crop processing failed:', error);
      setIsUploading(false);
      let errorMessage = 'Failed to process cropped image. Please try again.';
      if (error?.message?.includes('createImageBitmap')) {
        errorMessage = 'Your browser does not fully support image cropping. Please use a modern browser like Chrome, Firefox, or Safari.';
      } else if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error?.message?.includes('Cloudinary')) {
        errorMessage = 'Upload failed. Please check your connection and try again.';
      }
      toast.error(errorMessage);
    }
  }, [imageFile, sourceBlob, croppedAreaPixels, rotation, maxSize, onCroppedBlob, uploadFolder, autoUpload, isPreviewMode, onCropComplete, onClose]);

  console.log('ImageCropModal render:', { isOpen, imageFile: !!imageFile, imageUrl: !!imageUrl, loadingImage });
  
  if (!isOpen) {
    console.log('ImageCropModal: Not open, returning null');
    return null;
  }

  return createPortal(
    <div className="crop-modal-overlay" onClick={onClose}>
      <div className="crop-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crop-modal-header">
          <h3>Crop Image</h3>
          <button className="crop-modal-close" onClick={onClose} disabled={isUploading} aria-label="Close crop modal">
            <IconX size={20} />
          </button>
        </div>

        <div className="crop-modal-body">
          <div className="crop-container" aria-label="Image crop area">
            {!imageFile ? (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                <div className="spinner" />
                <span style={{ marginLeft: '12px' }}>Preparing image...</span>
              </div>
            ) : loadingImage ? (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.8)' }}>
                <div className="spinner" />
                <span style={{ marginLeft: '12px', color: '#666' }}>Loading image...</span>
              </div>
            ) : imageUrl ? (
              <>
                <Cropper
                  key={imageUrl} // Force re-render when image changes
                  image={imageUrl}
                  crop={crop}
                  rotation={rotation}
                  zoom={zoom}
                  aspect={aspectRatio === 0 ? undefined : aspectRatio}
                  onCropChange={onCropChange}
                  onCropComplete={onCropAreaChange}
                  onZoomChange={onZoomChange}
                  cropShape={cropShape}
                  showGrid={aspectRatio === 0}
                  onMediaLoaded={() => {
                    console.log('ImageCropModal: Image loaded in Cropper');
                    setIsImageReady(true);
                  }}
                  restrictPosition={false}
                  zoomWithScroll={true}
                  minZoom={minZoom}
                  maxZoom={maxZoom}
                  style={{
                    containerStyle: {
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#f8f9fa'
                    }
                  }}
                />
                {!isImageReady && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)' }}>
                    <div className="spinner" />
                  </div>
                )}
              </>
            ) : (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                Failed to load image. Please try again.
              </div>
            )}
          </div>

          <div className="crop-controls">
            <div className="crop-control-group">
              <button
                className="crop-control-btn"
                onClick={() => setZoom(prev => Math.max(minZoom, prev - 0.1))}
                disabled={zoom <= minZoom || !isImageReady}
                aria-label="Zoom out"
              >
                <IconZoomOut size={16} />
              </button>
              <input
                type="range"
                min={minZoom}
                max={maxZoom}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="crop-zoom-slider"
                aria-label="Zoom level"
                disabled={!isImageReady}
              />
              <button
                className="crop-control-btn"
                onClick={() => setZoom(prev => Math.min(maxZoom, prev + 0.1))}
                disabled={zoom >= maxZoom || !isImageReady}
                aria-label="Zoom in"
              >
                <IconZoomIn size={16} />
              </button>
              <span className="crop-zoom-label" aria-live="polite">{Math.round(zoom * 100)}%</span>
            </div>

            <div className="crop-control-group">
              <button 
                className="crop-control-btn" 
                onClick={rotateLeft} 
                disabled={!isImageReady}
                aria-label="Rotate left"
              >
                <IconChevronLeft size={16} />
              </button>
              <span className="crop-rotation-label">{rotation}°</span>
              <button 
                className="crop-control-btn" 
                onClick={rotateRight} 
                disabled={!isImageReady}
                aria-label="Rotate right"
              >
                <IconChevronRight size={16} />
              </button>
              <button 
                className="crop-control-btn" 
                onClick={() => setRotation(0)}
                disabled={rotation === 0 || !isImageReady}
                aria-label="Reset rotation"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="crop-modal-footer">
          <button
            className="crop-cancel-btn"
            onClick={onClose}
            disabled={isUploading}
          >
            <IconX size={16} />
            Cancel
          </button>
          <button
            className="crop-complete-btn"
            onClick={handleCropComplete}
            disabled={isUploading || !imageFile || !isImageReady}
          >
            {isUploading ? (
              <>
                <div className="spinner" />
                {isPreviewMode ? 'Cropping...' : 'Uploading...'}
              </>
            ) : (
              <>
                <IconCheck size={16} />
                {isPreviewMode ? 'Crop' : (autoUpload ? 'Upload' : 'Done')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Helper: crop image using react-easy-crop -> Blob
const getCroppedImage = async (imageFile, croppedAreaPixels, rotation = 0, maxSize = { width: 512, height: 512 }) => {
  // Try to obtain an image source that Canvas can draw: ImageBitmap or HTMLImageElement
  const loadImage = async (file) => {
    if ('createImageBitmap' in window && typeof createImageBitmap === 'function') {
      try { return await createImageBitmap(file); } catch {}
    }
    const url = URL.createObjectURL(file);
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
      img.src = url;
    });
  };

  const image = await loadImage(imageFile);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const { width, height } = croppedAreaPixels;

  canvas.width = width;
  canvas.height = height;

  ctx.translate(width / 2, height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-width / 2, -height / 2);

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    croppedAreaPixels.width,
    croppedAreaPixels.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
};

export default ImageCropModal;
